import { Fragment, type ReactNode } from 'react';

/**
 * Severity order for every icon this zone can hold, most severe first. Later status icons slot into
 * this list rather than deciding their own placement. A slot is a rank, not an owner: device alerts
 * share `monitoringError` and `info` with any other producer (see `ALERT_SEVERITY_ICON_SLOT`).
 */
export const DEVICE_STATUS_ICON_SEVERITY = [
  'monitoringError',
  'offlineSuppression',
  'activeSuppression',
  'alertWarning',
  'info',
] as const;

export type DeviceStatusIconSeverity = (typeof DEVICE_STATUS_ICON_SEVERITY)[number];

export type DeviceStatusIconEntry = {
  key: string;
  severity: DeviceStatusIconSeverity;
  render: (options: { animated: boolean }) => ReactNode;
};

type DeviceStatusIconZoneProps = {
  icons: DeviceStatusIconEntry[];
};

/**
 * Only the most severe icon is granted the animation slot, so a card never carries two competing
 * pulses. A render callback rather than children: with children the zone could not tell an icon
 * whether it won the slot, so the rule would have to be restated in every icon.
 */
export const DeviceStatusIconZone = ({ icons }: DeviceStatusIconZoneProps) => {
  const orderedIcons = [...icons].sort(
    (first, second) =>
      DEVICE_STATUS_ICON_SEVERITY.indexOf(first.severity) -
      DEVICE_STATUS_ICON_SEVERITY.indexOf(second.severity),
  );

  return (
    <div data-test-id="device-status-icons" className="flex h-8 items-center gap-1 pl-6">
      {orderedIcons.map((icon, index) => (
        <Fragment key={icon.key}>{icon.render({ animated: index === 0 })}</Fragment>
      ))}
    </div>
  );
};
